import { FC, useEffect, useState, useRef } from 'react';
import { useBatteryStatus } from '../../hooks/useBatteryStatus';
import { playAlertSequence } from '../../utils/beep';
import { BatteryAlertOverlay } from './BatteryAlertOverlay';
import { VolumeService } from '../../services/volume/VolumeService';

const CONFIG_KEY = 'efflux_player_config';

interface PlayerConfig {
  volumeLevel: number;
  resetVolumeTimeoutSeconds: number;

  batteryWarningEnabled?: boolean;
  audioAlertEnabled?: boolean;
  audioAlertVolumeLevel?: number;
  audioAlertBeepCount?: number;
  audioAlertIntervalMinutes?: number;
  audioAlertLowBatteryBeepCount?: number;
  audioAlertLowBatteryIntervalMinutes?: number;
  visualAlertEnabled?: boolean;
  visualAlertBlinkCount?: number;
  visualAlertDurationSeconds?: number;
  visualAlertLowBatteryBlinkCount?: number;
  visualAlertLowBatteryDurationSeconds?: number;
}

export const BatteryMonitor: FC = () => {
  const { batteryStatus, isSupported } = useBatteryStatus();
  const [showVisualAlert, setShowVisualAlert] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);

  const volumeService = useRef(VolumeService.getInstance());
  const previousChargingRef = useRef<boolean | null>(null);

  // Refs to track timers
  const cycleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visualTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const blinkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to get config
  const getConfig = (): PlayerConfig => {
    const stored = localStorage.getItem(CONFIG_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('[BatteryMonitor] Failed to parse config', e);
      }
    }
    // Defaults
    return {
      volumeLevel: 50,
      resetVolumeTimeoutSeconds: 0,
      batteryWarningEnabled: true,
      audioAlertEnabled: true,
      audioAlertVolumeLevel: 80,
      audioAlertBeepCount: 3,
      audioAlertIntervalMinutes: 5,
      audioAlertLowBatteryBeepCount: 5,
      audioAlertLowBatteryIntervalMinutes: 3,
      visualAlertEnabled: true,
      visualAlertBlinkCount: 3,
      visualAlertDurationSeconds: 30,
      visualAlertLowBatteryBlinkCount: 5,
      visualAlertLowBatteryDurationSeconds: 60,
    };
  };

  const triggerAlert = async () => {
    const config = getConfig();
    if (!config.batteryWarningEnabled) return;

    const isLowBattery = (batteryStatus?.level ?? 1) < 0.2; // < 20%

    console.log(
      `[BatteryMonitor] Triggering Alert. Level: ${batteryStatus?.level}, Low: ${isLowBattery}`,
    );

    // 1. Audio Alert
    if (config.audioAlertEnabled) {
      const beepCount = isLowBattery
        ? (config.audioAlertLowBatteryBeepCount ?? 5)
        : (config.audioAlertBeepCount ?? 3);
      const targetVolume = config.audioAlertVolumeLevel ?? 80;

      // Force Volume Logic
      try {
        const currentVolume = await volumeService.current.getVolume();
        console.log(
          `[BatteryMonitor] Saving volume: ${currentVolume}, Setting to: ${targetVolume}`,
        );

        await volumeService.current.setVolume(targetVolume);

        // Play sequence
        await playAlertSequence(beepCount);

        // Restore volume
        // Wait a bit to ensure beep finished if playAlertSequence is not perfectly awaiting audio end
        // But playAlertSequence should be awaited.
        console.log(`[BatteryMonitor] Restoring volume to: ${currentVolume}`);
        await volumeService.current.setVolume(currentVolume);
      } catch (e) {
        console.error('[BatteryMonitor] Error in audio sequence', e);
      }
    }

    // 2. Visual Alert
    if (config.visualAlertEnabled) {
      const durationSec = isLowBattery
        ? (config.visualAlertLowBatteryDurationSeconds ?? 60)
        : (config.visualAlertDurationSeconds ?? 30);
      const _blinkCount = isLowBattery
        ? (config.visualAlertLowBatteryBlinkCount ?? 5)
        : (config.visualAlertBlinkCount ?? 3);

      // 2.5s is approx duration of blink animation (5 * 0.5s) in CSS?
      // We need to adjust blink duration based on blinkCount if we want exact control,
      // but CSS usually has fixed animation.
      // For now, let's just show the overlay.

      setShowVisualAlert(true);
      setIsBlinking(true);

      // Stop blinking after some time (or based on count if we could control CSS)
      // Let's assume standard blink time for now or just let it blink for a few seconds
      if (blinkTimeoutRef.current) clearTimeout(blinkTimeoutRef.current);
      blinkTimeoutRef.current = setTimeout(() => {
        setIsBlinking(false);
      }, 5000); // 5 seconds of blink

      if (visualTimeoutRef.current) clearTimeout(visualTimeoutRef.current);
      visualTimeoutRef.current = setTimeout(() => {
        setShowVisualAlert(false);
      }, durationSec * 1000);
    }

    // Schedule Next Alert
    const intervalMinutes = isLowBattery
      ? (config.audioAlertLowBatteryIntervalMinutes ?? 3)
      : (config.audioAlertIntervalMinutes ?? 5);
    const intervalMs = intervalMinutes * 60 * 1000;

    console.log(
      `[BatteryMonitor] Scheduling next alert in ${intervalMinutes} minutes`,
    );

    if (cycleTimeoutRef.current) clearTimeout(cycleTimeoutRef.current);
    cycleTimeoutRef.current = setTimeout(() => {
      triggerAlert();
    }, intervalMs);
  };

  const stopMonitoring = () => {
    if (cycleTimeoutRef.current) {
      clearTimeout(cycleTimeoutRef.current);
      cycleTimeoutRef.current = null;
    }
    if (visualTimeoutRef.current) {
      clearTimeout(visualTimeoutRef.current);
      visualTimeoutRef.current = null;
    }
    if (blinkTimeoutRef.current) {
      clearTimeout(blinkTimeoutRef.current);
      blinkTimeoutRef.current = null;
    }
    setShowVisualAlert(false);
    setIsBlinking(false);
  };

  useEffect(() => {
    if (!isSupported || !batteryStatus) return;

    const wasCharging = previousChargingRef.current;

    // If NOT charging -> Start or restart on transition
    if (!batteryStatus.charging) {
      if (wasCharging !== false) {
        console.log(
          '[BatteryMonitor] Power disconnected. Starting monitoring.',
        );
        stopMonitoring();
        triggerAlert();
      } else if (!cycleTimeoutRef.current) {
        console.log(
          '[BatteryMonitor] Power disconnected. Resuming monitoring.',
        );
        triggerAlert();
      }
    } else {
      // Charging
      if (cycleTimeoutRef.current || showVisualAlert || wasCharging === false) {
        console.log('[BatteryMonitor] Power connected. Stopping monitoring.');
        stopMonitoring();
      }
    }

    // We do NOT add batteryStatus.level to dependency array to avoid restarting loop constantly.
    // The loop itself checks level.
    // We only care about charging state changes.
    previousChargingRef.current = batteryStatus.charging;
  }, [batteryStatus?.charging, isSupported]);

  useEffect(() => {
    return () => stopMonitoring();
  }, []);

  return (
    <BatteryAlertOverlay isVisible={showVisualAlert} isBlinking={isBlinking} />
  );
};
